import {
  getAllSharingExceptions,
  createSharingException,
  updateSharingException,
  deleteSharingException,
} from 'common/src/api/sharing-permissions';
import { getAllExporters } from 'common/src/api/exporters';
import { getAllSpecies } from 'common/src/api/species';
import { SharingPermissionException, Exporter, Species } from 'common/src/utils/types';
import { SEASONS } from 'common/src/utils/definitions';
import { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Card,
  CardBody,
  Skeleton,
  Chip,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import Header from '../../../components/layouts/Header';
import { ConfirmModal, PageLayout } from '../../../components/ui';

const SuperadminSharingPermissions = () => {
  const [exceptions, setExceptions] = useState<(SharingPermissionException & { exporter?: Exporter; species?: Species })[]>([]);
  const [exporters, setExporters] = useState<Exporter[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedException, setSelectedException] = useState<SharingPermissionException | null>(null);
  const [newException, setNewException] = useState<{
    exporter_id: string;
    species_id: string;
    season: string;
  }>({
    exporter_id: '',
    species_id: '',
    season: '',
  });

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isNewOpen,
    onOpen: onNewOpen,
    onClose: onNewClose,
  } = useDisclosure();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [fetchedExceptions, fetchedExporters, fetchedSpecies] = await Promise.all([
        getAllSharingExceptions(),
        getAllExporters(),
        getAllSpecies(),
      ]);
      setExceptions(fetchedExceptions);
      // Sort exporters and species alphabetically by name
      setExporters(fetchedExporters.sort((a, b) => a.name.localeCompare(b.name)));
      setSpecies(fetchedSpecies.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (exception: SharingPermissionException) => {
    setSelectedException(exception);
    onEditOpen();
  };

  const handleDelete = (exception: SharingPermissionException) => {
    setSelectedException(exception);
    onDeleteOpen();
  };

  const handleEditSubmit = async () => {
    if (!selectedException) return;
    try {
      await updateSharingException(selectedException.id, {
        season: selectedException.season || null,
      });
      toast.success('Excepción actualizada correctamente');
      onEditClose();
      fetchData();
    } catch (error) {
      console.error('Error updating exception:', error);
      toast.error('Error al actualizar excepción');
    }
  };

  const handleNewSubmit = async () => {
    try {
      if (!newException.exporter_id || !newException.species_id) {
        toast.error('Exportadora y Especie son obligatorios');
        return;
      }

      const exporterId = parseInt(newException.exporter_id);
      const speciesId = parseInt(newException.species_id);
      const season = newException.season || null;

      // Check for conflicting exceptions
      const existingExceptions = exceptions.filter(
        (exc) => exc.exporter_id === exporterId && exc.species_id === speciesId
      );

      if (existingExceptions.length > 0) {
        // Case 1: Trying to add a specific season, but "all seasons" already exists
        if (season && existingExceptions.some((exc) => !exc.season)) {
          toast.error(
            'Ya existe una excepción para TODAS las temporadas. No puedes agregar una temporada específica.'
          );
          return;
        }

        // Case 2: Trying to add "all seasons", but specific seasons already exist
        if (!season && existingExceptions.some((exc) => exc.season)) {
          toast.error(
            'Ya existen excepciones para temporadas específicas. Elimínalas primero si quieres bloquear todas las temporadas.'
          );
          return;
        }
      }

      await createSharingException({
        exporter_id: exporterId,
        species_id: speciesId,
        season: season,
      });

      toast.success('Excepción creada correctamente');
      onNewClose();
      setNewException({ exporter_id: '', species_id: '', season: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating exception:', error);
      toast.error('Error al crear excepción. Puede que ya exista.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedException) return;
    try {
      await deleteSharingException(selectedException.id);
      toast.success('Excepción eliminada correctamente');
      onDeleteClose();
      fetchData();
    } catch (error) {
      console.error('Error deleting exception:', error);
      toast.error('Error al eliminar excepción');
    }
  };

  const columns = [
    { name: 'ID', uid: 'id' },
    { name: 'Exportadora', uid: 'exporter' },
    { name: 'Especie', uid: 'species' },
    { name: 'Temporada', uid: 'season' },
    { name: 'Acciones', uid: 'actions' },
  ];

  const renderCell = (exception: SharingPermissionException & { exporter?: Exporter; species?: Species }, columnKey: React.Key) => {
    switch (columnKey) {
      case 'id':
        return exception.id;
      case 'exporter':
        return exception.exporter?.name || `ID: ${exception.exporter_id}`;
      case 'species':
        return exception.species?.name || `ID: ${exception.species_id}`;
      case 'season':
        return exception.season ? (
          <Chip size='sm' variant='flat'>
            {exception.season}
          </Chip>
        ) : (
          <Chip size='sm' variant='flat' color='warning'>
            Todas las temporadas
          </Chip>
        );
      case 'actions':
        return (
          <div className='flex items-center gap-2'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => handleEdit(exception)}
            >
              <Icon icon='tabler:edit' className='text-default-400' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color='danger'
              onClick={() => handleDelete(exception)}
            >
              <Icon icon='tabler:trash' className='text-danger-400' />
            </Button>
          </div>
        );
      default:
        return String(exception[columnKey as keyof SharingPermissionException] || '');
    }
  };

  // Get season options from SEASONS definition (format: "2024-2025")
  const seasonOptions = Object.values(SEASONS)
    .map(season => season.name)
    .reverse(); // Most recent seasons first

  return (
    <PageLayout>
      <Header
        title='Excepciones de Compartición - Módulo Industria'
        description='Exportadoras que NO comparten datos en el módulo Industria'
        actions={
          <Button color='primary' onClick={onNewOpen}>
            Añadir Excepción
          </Button>
        }
      />

      {loading ? (
        <Card className='w-full'>
          <CardBody>
            <div className='space-y-4'>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className='h-12 w-full rounded-lg' />
              ))}
            </div>
          </CardBody>
        </Card>
      ) : (
        <Table removeWrapper aria-label='Sharing exceptions table'>
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn key={column.uid}>{column.name}</TableColumn>
            )}
          </TableHeader>
          <TableBody items={exceptions} emptyContent='No hay excepciones configuradas'>
            {(item) => (
              <TableRow key={item.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalContent>
          <ModalHeader>Editar Excepción</ModalHeader>
          <ModalBody>
            <p className='text-sm text-default-500 mb-4'>
              Solo se puede editar la temporada. Para cambiar exportadora o especie, elimine y cree una nueva excepción.
            </p>
            <Select
              label='Temporada'
              placeholder='Todas las temporadas'
              selectedKeys={selectedException?.season ? [selectedException.season] : []}
              onChange={(e) =>
                setSelectedException({
                  ...selectedException!,
                  season: e.target.value || null,
                })
              }
            >
              <SelectItem key='' value=''>
                Todas las temporadas
              </SelectItem>
              {seasonOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onEditClose}>
              Cancelar
            </Button>
            <Button
              color='primary'
              onClick={handleEditSubmit}
            >
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title='Confirmar Eliminación'
        confirmLabel='Eliminar'
        isDestructive
      >
        ¿Está seguro de eliminar esta excepción? La exportadora volverá a compartir sus datos en el módulo Industria.
      </ConfirmModal>

      {/* New Exception Modal */}
      <Modal isOpen={isNewOpen} onClose={onNewClose} size='lg'>
        <ModalContent>
          <ModalHeader>Nueva Excepción de Compartición</ModalHeader>
          <ModalBody>
            <p className='text-sm text-default-500 mb-4'>
              La exportadora NO podrá acceder ni aparecer en los datos del módulo Industria para la especie seleccionada.
            </p>
            <Autocomplete
              label='Exportadora'
              placeholder='Buscar exportadora...'
              selectedKey={newException.exporter_id || null}
              onSelectionChange={(key) =>
                setNewException({ ...newException, exporter_id: key?.toString() || '' })
              }
              isRequired
              classNames={{
                base: 'max-w-full',
              }}
            >
              {exporters.map((exp) => (
                <AutocompleteItem key={exp.id.toString()} value={exp.id.toString()}>
                  {exp.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>

            <Autocomplete
              label='Especie'
              placeholder='Buscar especie...'
              selectedKey={newException.species_id || null}
              onSelectionChange={(key) =>
                setNewException({ ...newException, species_id: key?.toString() || '' })
              }
              isRequired
              classNames={{
                base: 'max-w-full',
              }}
            >
              {species.map((sp) => (
                <AutocompleteItem key={sp.id.toString()} value={sp.id.toString()}>
                  {sp.name}
                </AutocompleteItem>
              ))}
            </Autocomplete>

            <Select
              label='Temporada (Opcional)'
              placeholder='Todas las temporadas'
              selectedKeys={newException.season ? [newException.season] : []}
              onChange={(e) =>
                setNewException({ ...newException, season: e.target.value })
              }
            >
              <SelectItem key='' value=''>
                Todas las temporadas
              </SelectItem>
              {seasonOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onNewClose}>
              Cancelar
            </Button>
            <Button
              color='primary'
              onClick={handleNewSubmit}
            >
              Crear
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminSharingPermissions;
